import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOnboarding } from './OnboardingContext';
import DemoPreview from './components/DemoPreview';
import CalendarMockup from './components/CalendarMockup';
import ProviderConnectModal from './components/ProviderConnectModal';
import { Button } from '@/components/ui/button';
import { CalendarDays } from 'lucide-react';
import { getGoogleIntegrationStatus, getMicrosoftIntegrationStatus, startGoogleOAuth } from '../../lib/api';
import { finalizeRealConnectionAndEnter } from './finalizeRealConnection';

/**
 * Step 2 — Calendar. Same reconciliation contract as StepInbox: the
 * backend (GET /api/integrations/google/status) is checked on mount,
 * BEFORE any connect UI renders, so a user who lands here directly
 * (back-navigation, refresh, bookmark) while already connected never
 * sees "Conectar" again — they're routed straight to /inbox instead.
 */
export default function StepCalendar() {
  const { t } = useLanguage();
  const { markStepConnected, markStepSkipped, reset } = useOnboarding();
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [pickerOpen, setPickerOpen] = useState(false);
  // 'checking' | 'ready' | 'permission_missing' | 'finalizing'
  const [reconcileState, setReconcileState] = useState('checking');
  const [missingScopesLabel, setMissingScopesLabel] = useState('');
  const reauthorizingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [g, m] = await Promise.all([
        getGoogleIntegrationStatus().catch(() => null),
        getMicrosoftIntegrationStatus().catch(() => null),
      ]);
      if (cancelled) return;

      const googleOk = !!g?.connected && g?.status === 'ok' && !g?.reauthorization_required;
      const msOk = !!m?.connected && m?.status === 'ok' && !m?.reauthorization_required;
      if (googleOk || msOk) {
        setReconcileState('finalizing');
        markStepConnected('inbox', 'real');
        markStepConnected('calendar', 'real');
        await finalizeRealConnectionAndEnter({ refresh, navigate, resetOnboarding: reset });
        return;
      }

      const needsReauth = g?.configured && (g?.reauthorization_required || g?.status === 'permission_missing');
      if (needsReauth) {
        const scopes = (g?.missing_scopes || [])
          .map((s) => s.split('/').pop().replace(/\./g, ' '))
          .join(', ');
        setMissingScopesLabel(scopes);
        setReconcileState('permission_missing');
        return;
      }

      setReconcileState('ready');
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReauthorize = async () => {
    if (reauthorizingRef.current) return;
    reauthorizingRef.current = true;
    try {
      const { auth_url } = await startGoogleOAuth('/welcome/calendar');
      if (auth_url) window.location.href = auth_url;
    } finally {
      reauthorizingRef.current = false;
    }
  };

  // If either Google or Microsoft is already connected from StepInbox
  // (the OAuth scopes for Mail include Calendar in both providers),
  // we don't bother running OAuth again — just finalize like the mount
  // check above does.
  const handleConnect = async () => {
    try {
      const [g, m] = await Promise.all([
        getGoogleIntegrationStatus().catch(() => null),
        getMicrosoftIntegrationStatus().catch(() => null),
      ]);
      const alreadyConnected = (g?.connected && g?.status === 'ok') || (m?.connected && m?.status === 'ok');
      if (alreadyConnected) {
        markStepConnected('inbox', 'real');
        markStepConnected('calendar', 'real');
        await finalizeRealConnectionAndEnter({ refresh, navigate, resetOnboarding: reset });
        return;
      }
    } catch {
      /* status calls failed — fall through and let the user pick. */
    }

    // Open picker. Promise stays pending so DemoPreview keeps its
    // 'connecting' spinner while the user decides.
    return new Promise((resolve) => {
      setPickerOpen(true);
      window.__quantroPickerResolve = resolve;
    });
  };

  const handleOpenChange = (open) => {
    setPickerOpen(open);
    if (!open) {
      window.__quantroPickerResolve?.();
      window.__quantroPickerResolve = null;
    }
  };

  const handleSkip = () => {
    markStepSkipped('calendar');
    markStepConnected('calendar', 'demo');
    navigate('/welcome/crm');
  };

  if (reconcileState === 'checking' || reconcileState === 'finalizing') {
    return (
      <div className="flex flex-col items-center justify-center text-center min-h-[50vh]" data-testid="step-calendar-reconciling">
        <Loader2 size={28} className="animate-spin text-[hsl(var(--primary))] mb-4" />
        <p className="text-sm text-muted-foreground">{t('welcome.inbox.checking_connection')}</p>
      </div>
    );
  }

  if (reconcileState === 'permission_missing') {
    return (
      <div className="flex flex-col items-center text-center max-w-lg mx-auto" data-testid="step-calendar-permission-missing">
        <div className="size-16 rounded-2xl bg-[hsl(var(--warning)/0.10)] text-[hsl(var(--warning))] flex items-center justify-center mb-6">
          <ShieldAlert size={28} />
        </div>
        <h1 className="font-display text-2xl md:text-4xl font-semibold tracking-tight mb-3 text-balance">
          {t('welcome.connect_modal.permission_missing_title')}
        </h1>
        <p className="text-base text-muted-foreground mb-8 leading-relaxed text-balance">
          {t('welcome.connect_modal.permission_missing_desc', { provider: 'Google', scopes: missingScopesLabel })}
        </p>
        <Button onClick={handleReauthorize} data-testid="step-calendar-reauthorize-button">
          {t('welcome.connect_modal.reauthorize_cta')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center" data-testid="step-calendar-page">
      <div className="size-16 rounded-2xl bg-[hsl(var(--primary)/0.10)] text-[hsl(var(--primary))] flex items-center justify-center mb-6">
        <CalendarDays size={28} />
      </div>
      <h1 className="font-display text-3xl md:text-5xl font-semibold tracking-tight mb-3 text-balance" data-testid="step-calendar-title">
        {t('welcome.calendar.title')}
      </h1>
      <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed text-balance whitespace-pre-line">
        {t('welcome.calendar.subtitle')}
      </p>

      <DemoPreview
        testIdPrefix="step-calendar"
        microcopyPreview={t('welcome.calendar.preview_microcopy')}
        microcopyEnd={t('welcome.calendar.preview_end_microcopy')}
        connectLabel={t('welcome.calendar.connect_cta')}
        skipLabel={t('welcome.calendar.skip_cta')}
        phases={[
          { key: 'syncing',   delay_ms: 0,    label: t('welcome.calendar.phase_syncing')   },
          { key: 'detecting', delay_ms: 1800, label: t('welcome.calendar.phase_detecting') },
          { key: 'aligning',  delay_ms: 3600, label: t('welcome.calendar.phase_aligning')  },
        ]}
        previewDurationMs={7000}
        onConnect={handleConnect}
        onSkip={handleSkip}
      >
        <CalendarMockup />
      </DemoPreview>

      <ProviderConnectModal
        open={pickerOpen}
        onOpenChange={handleOpenChange}
        step="calendar"
        returnTo="/welcome/calendar"
        onAlreadyConnected={() => {
          markStepConnected('inbox', 'real');
          markStepConnected('calendar', 'real');
          finalizeRealConnectionAndEnter({ refresh, navigate, resetOnboarding: reset });
        }}
      />
    </div>
  );
}
